import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import type { ItilTranslationStatus } from '../types';
import { itilColors, itilTranslateBtnSx } from '../styles';

type Props = {
  status: ItilTranslationStatus;
  onTranslate: () => void;
  onContinueOriginal?: () => void;
  onContinueWithoutItil?: () => void;
  disabled?: boolean;
  hint?: string | null;
};

function ItilTranslateActions({
  status,
  onTranslate,
  onContinueOriginal,
  onContinueWithoutItil,
  disabled = false,
  hint
}: Props) {
  const { t } = useTranslation('global');

  const buttonLabel = () => {
    if (status === 'ready' || status === 'stale') {
      return t('itilGenius.actions.retranslate');
    }
    if (status === 'error') {
      return t('itilGenius.actions.retry');
    }
    if (status === 'skipped' || status === 'storedAsOriginal') {
      return t('itilGenius.actions.tryTranslate');
    }
    return t('itilGenius.actions.translate');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        mt: 1.5,
        flexWrap: 'wrap'
      }}
    >
      {hint && (
        <Typography
          sx={{
            fontSize: '11.5px',
            color: status === 'stale' ? itilColors.req : itilColors.inkSoft,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5
          }}
          variant="body2"
        >
          {hint}
        </Typography>
      )}

      {status === 'error' && onContinueOriginal && (
        <Button
          onClick={onContinueOriginal}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '11.5px',
            color: '#8a322c',
            borderColor: '#d9b2af'
          }}
          variant="outlined"
        >
          {t('itilGenius.actions.continueOriginal')}
        </Button>
      )}

      {status === 'error' && onContinueWithoutItil && (
        <Button
          onClick={onContinueWithoutItil}
          size="small"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '11.5px',
            color: '#8a322c',
            borderColor: '#d9b2af'
          }}
          variant="outlined"
        >
          {t('itilGenius.actions.continueWithoutItil')}
        </Button>
      )}

      <Box sx={{ flex: 1 }} />

      <Button
        disabled={disabled || status === 'loading'}
        onClick={onTranslate}
        size="small"
        sx={itilTranslateBtnSx}
        variant="outlined"
      >
        ↻ {buttonLabel()}
      </Button>
    </Box>
  );
}

export default ItilTranslateActions;
